/**
 * Nighttime closure — a per-location daily window in which NO room may be booked by ANYONE.
 *
 * MIRROR of ember/src/utils/nightClosure.ts. The server is the authority (see
 * `blockIfWithinNightClosure` in bookingRoutes); this copy exists so the calendar can intercept a
 * click before a doomed request, and so the settings form can validate. Keep the two in step — the
 * boundary semantics below are what users notice, and a divergence would show up as a click the UI
 * accepts and the server then refuses.
 *
 * Unlike `location.bookingLocked` (which members are blocked by and elevated users bypass), this
 * closure has **no bypass**: admins and practice managers are refused too. The building is shut.
 *
 * The window WRAPS MIDNIGHT — a closure of 23:00→06:00 means "closed" is
 * `[23:00, 24:00) ∪ [00:00, 06:00)`. Every comparison here is time-of-day only, in minutes from
 * midnight, so it applies identically to every date and needs no timezone reasoning.
 *
 * Boundary semantics, which are what users actually notice:
 *   - START is INCLUSIVE  — with a 23:00 start, a booking running to 23:30 is refused.
 *   - END is EXCLUSIVE    — with an 06:00 end, an 06:00 start is ALLOWED. This is precisely the
 *                           "6am needs to be the first available start time" requirement.
 */

export interface NightClosure {
  enabled?: boolean;
  startTime?: string; // "HHmm"
  endTime?: string; // "HHmm"
}

/** Selectable bounds for the settings UI. Half-hour granularity. */
export const NIGHT_CLOSURE_START_MIN = "1900";
export const NIGHT_CLOSURE_START_MAX = "2330";
export const NIGHT_CLOSURE_END_MIN = "0500";
export const NIGHT_CLOSURE_END_MAX = "0800";

/** "HHmm" → minutes from midnight, or null when unparseable. */
export function toMinutes(time: string | undefined | null): number | null {
  if (typeof time !== "string") return null;
  // Guard BEFORE padding: "".padStart(4, "0") is "0000", which would silently become midnight.
  if (time.trim() === "") return null;
  const padded = time.padStart(4, "0");
  if (!/^\d{4}$/.test(padded)) return null;
  const hours = parseInt(padded.slice(0, 2), 10);
  const mins = parseInt(padded.slice(2), 10);
  if (hours > 23 || mins > 59) return null;
  return hours * 60 + mins;
}

/**
 * Is the closure configured well enough to enforce? A half-configured closure (enabled with a
 * missing or malformed time) is treated as OFF rather than as an accidental all-day lockout.
 */
export function isClosureActive(closure: NightClosure | null | undefined): boolean {
  if (!closure?.enabled) return false;
  const start = toMinutes(closure.startTime);
  const end = toMinutes(closure.endTime);
  return start !== null && end !== null && start !== end;
}

/**
 * Does a booking of [startTime, endTime) fall even partly inside the closure?
 *
 * `endTime` is the booking's exclusive end. A booking that merely touches the closure boundary
 * (ends exactly at closure start, or starts exactly at closure end) does NOT overlap.
 *
 * A booking whose own end is <= its start is treated as spanning midnight and is refused whenever a
 * closure is active — a booking running through the small hours cannot avoid a nighttime closure.
 */
export function isWithinNightClosure(
  closure: NightClosure | null | undefined,
  startTime: string,
  endTime: string,
): boolean {
  if (!isClosureActive(closure)) return false;

  const closureStart = toMinutes(closure!.startTime)!;
  const closureEnd = toMinutes(closure!.endTime)!;
  const bookingStart = toMinutes(startTime);
  // "2400" is a booking ending at midnight (end of THIS day) — toMinutes rejects hour 24.
  const bookingEnd = endTime?.padStart(4, "0") === "2400" ? 24 * 60 : toMinutes(endTime);

  // Unparseable booking times: fail CLOSED. We cannot prove it is outside the window, and silently
  // permitting is the worse failure for a rule framed as "nobody may book".
  if (bookingStart === null || bookingEnd === null) return true;

  // The closed window as one or two non-wrapping intervals on a single day.
  const closedIntervals: Array<[number, number]> =
    closureStart < closureEnd
      ? [[closureStart, closureEnd]] // same-day window (not a night closure, but handled)
      : [
          [closureStart, 24 * 60], // evening portion
          [0, closureEnd], // morning portion
        ];

  // A booking that ends at or before it starts runs through midnight; treat it as covering the
  // whole remainder of the day plus the start of the next.
  const bookingIntervals: Array<[number, number]> =
    bookingEnd > bookingStart
      ? [[bookingStart, bookingEnd]]
      : [
          [bookingStart, 24 * 60],
          [0, bookingEnd],
        ];

  return bookingIntervals.some(([bStart, bEnd]) =>
    closedIntervals.some(([cStart, cEnd]) => bStart < cEnd && bEnd > cStart),
  );
}

/** "HHmm" plus N minutes, wrapping past midnight. Used to turn a clicked start slot into a range. */
export function addMinutes(time: string, minutes: number): string {
  const base = toMinutes(time);
  if (base === null) return time;
  const total = ((base + minutes) % (24 * 60) + 24 * 60) % (24 * 60);
  return `${Math.floor(total / 60).toString().padStart(2, "0")}${(total % 60).toString().padStart(2, "0")}`;
}

/** Human-readable window, e.g. "23:00–06:00", for messages and settings copy. */
export function formatClosureWindow(closure: NightClosure | null | undefined): string {
  if (!isClosureActive(closure)) return "";
  const fmt = (t: string) => `${t.padStart(4, "0").slice(0, 2)}:${t.padStart(4, "0").slice(2)}`;
  return `${fmt(closure!.startTime!)}–${fmt(closure!.endTime!)}`;
}
